/**
 * BulkAssignLocationPanel — bulk-assign a single primary location (center) to the
 * selected team members.
 *
 * 80%-width slide-in drawer to pick ONE location. If some selected members already
 * have a location, the user chooses Transfer (move them from their previous center
 * to the new one) or Skip (only set the location on members that don't have one).
 *
 * Mirrors components/clients/BulkBcbaPanel.tsx.
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled, { keyframes } from 'styled-components';
import { X, Check, Search, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { media } from '../../styles/media';
import { getLocations, type Location } from '../../services/api/locationService';
import memberService, {
  type LocationConflictResolution,
  type BulkLocationResult,
} from '../../services/api/memberService';

interface BulkAssignLocationPanelProps {
  memberIds: string[];
  onClose: () => void;
  /** Called after a successful apply (parent clears selection + closes). */
  onDone: () => void;
}

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: ${({ theme }) => theme.zIndex.modalBackdrop};
`;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(80vw, 1280px);
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-left: 1px solid ${({ theme }) => theme.colors.borders.light};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} ${({ theme }) => theme.transitions.base} ease-out;

  ${media.desktop} {
    width: 90vw;
  }
  ${media.tablet} {
    width: 100vw;
  }
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    color: ${({ theme }) => theme.colors.text.primary};
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SearchBox = styled.div`
  position: relative;
  svg {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.text.light};
  }
  input {
    width: 100%;
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
    padding-left: 34px;
    border: 1px solid ${({ theme }) => theme.colors.borders.medium};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const ConflictBox = styled.div`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ConflictTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.status.warning.text};
`;

const ConflictNames = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing[5]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Choice = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing[2]};

  ${media.tablet} {
    grid-template-columns: 1fr;
  }
`;

const Row = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  width: 100%;
  text-align: left;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary.base : theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme, $selected }) =>
    $selected
      ? (theme.colors.primary.lightSky ?? theme.colors.backgrounds.light)
      : theme.colors.backgrounds.white};
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const Radio = styled.span<{ $checked?: boolean }>`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px solid
    ${({ theme, $checked }) => ($checked ? theme.colors.primary.base : theme.colors.borders.medium)};
  background: ${({ theme, $checked }) => ($checked ? theme.colors.primary.base : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  svg {
    width: 11px;
    height: 11px;
  }
`;

const RowInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  .meta {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    color: ${({ theme }) => theme.colors.text.secondary};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    svg {
      width: 11px;
      height: 11px;
    }
  }
`;

const StateBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const EmptyNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

const ResultBox = styled.div`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.status.success.bg};
  color: ${({ theme }) => theme.colors.status.success.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Footer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const locationLabel = (loc: Location) =>
  [loc.city, loc.state].filter(Boolean).join(', ') || 'No address';

const resultText = (r: BulkLocationResult): string => {
  const parts = [`Assigned ${r.assigned} member(s)`];
  if (r.transferred > 0) parts.push(`${r.transferred} transferred`);
  if (r.skipped > 0) parts.push(`${r.skipped} skipped`);
  return `${parts.join(' · ')}.`;
};

export function BulkAssignLocationPanel({
  memberIds,
  onClose,
  onDone,
}: BulkAssignLocationPanelProps) {
  const queryClient = useQueryClient();
  const memberCount = memberIds.length;

  const [search, setSearch] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [resolution, setResolution] = useState<LocationConflictResolution | null>(null);
  const [result, setResult] = useState<BulkLocationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    data: locationsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['locations', 'all-for-members'],
    queryFn: () => getLocations({ limit: 200 }),
  });

  const { data: preview } = useQuery({
    queryKey: ['members', 'bulk-location-preview', memberIds],
    queryFn: () => memberService.previewBulkLocation(memberIds),
    enabled: memberIds.length > 0,
    staleTime: 0,
    gcTime: 0,
  });

  const mutation = useMutation({
    mutationFn: memberService.bulkAssignLocation,
    onSuccess: (r) => {
      setResult(r);
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['seat-count'] });
    },
    onError: () => setError('Failed to assign location. Please try again.'),
  });

  const locations = useMemo(() => locationsData?.data ?? [], [locationsData]);
  const conflicts = preview?.withCenter ?? [];
  const conflictCount = conflicts.length;
  const hasConflicts = conflictCount > 0;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        (l.city ?? '').toLowerCase().includes(q) ||
        (l.state ?? '').toLowerCase().includes(q),
    );
  }, [locations, search]);

  const canSubmit = !!selectedLocationId && (!hasConflicts || resolution !== null);

  const submit = () => {
    if (!selectedLocationId) return;
    setError(null);
    mutation.mutate({
      memberIds,
      locationId: selectedLocationId,
      conflictResolution: hasConflicts ? (resolution ?? 'SKIP') : undefined,
    });
  };

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <Header>
          <div>
            <Title>Assign Location</Title>
            <Subtitle>
              to {memberCount} member{memberCount === 1 ? '' : 's'}
            </Subtitle>
          </div>
          <CloseBtn onClick={onClose} aria-label="Close">
            <X />
          </CloseBtn>
        </Header>

        <Body>
          {result ? (
            <ResultBox>{resultText(result)}</ResultBox>
          ) : (
            <>
              {hasConflicts && (
                <ConflictBox>
                  <ConflictTitle>
                    {conflictCount} of {memberCount} selected member
                    {memberCount === 1 ? '' : 's'} already have a location
                  </ConflictTitle>
                  <ConflictNames>
                    {conflicts.map((c) => (
                      <li key={c.memberId}>
                        {c.memberName} — {c.currentCenterName}
                      </li>
                    ))}
                  </ConflictNames>
                  <Choice>
                    <input
                      type="radio"
                      name="location-conflict"
                      checked={resolution === 'REPLACE'}
                      onChange={() => setResolution('REPLACE')}
                    />
                    Transfer them from their current location to the selected one
                  </Choice>
                  <Choice>
                    <input
                      type="radio"
                      name="location-conflict"
                      checked={resolution === 'SKIP'}
                      onChange={() => setResolution('SKIP')}
                    />
                    Skip them (only set the location on members without one)
                  </Choice>
                </ConflictBox>
              )}

              <SearchBox>
                <Search />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search a location by name or city..."
                />
              </SearchBox>

              {isLoading && (
                <StateBox>
                  <LoadingSpinner size="sm" /> Loading locations...
                </StateBox>
              )}
              {isError && !isLoading && <ErrorText>Failed to load locations.</ErrorText>}
              {!isLoading && !isError && filtered.length === 0 && (
                <EmptyNote>No matching locations.</EmptyNote>
              )}

              <List>
                {filtered.map((l) => {
                  const selected = selectedLocationId === l.id;
                  return (
                    <Row
                      key={l.id}
                      type="button"
                      $selected={selected}
                      onClick={() => setSelectedLocationId(l.id)}
                    >
                      <Radio $checked={selected}>{selected && <Check />}</Radio>
                      <RowInfo>
                        <div className="name">{l.name}</div>
                        <div className="meta">
                          <MapPin /> {locationLabel(l)}
                        </div>
                      </RowInfo>
                    </Row>
                  );
                })}
              </List>

              {error && <ErrorText>{error}</ErrorText>}
            </>
          )}
        </Body>

        <Footer>
          {result ? (
            <Button variant="primary" onClick={onDone}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!canSubmit}
                isLoading={mutation.isPending}
                onClick={submit}
              >
                Assign Location
              </Button>
            </>
          )}
        </Footer>
      </Panel>
    </Overlay>
  );
}

export default BulkAssignLocationPanel;
